import { CMS_EVENT_DETAILS_STORAGE_KEY, ODIN_LOGO_SWITCH_KEY } from "../../constants";
import EventHelper from "../../network/helpers/event/EventHelper";
import CMSHelper from "../cms/CMSHelper";
import store from "../../../redux/store";
import localForage from "localforage";
import { EventEditionDetails, ICMSApiResponse, IProduct, IPersonBadge, Nullable } from "../../types";
import { getStoredColocatedShowsForEvent } from "../../utils/colocatedShowsUtils";
import MagentoHelper from "../../network/helpers/magento/MagentoHelper";

class BadgeImageHelper {
  private _logoUrlAvailability = new Map<string, boolean>();

  shouldPrintBadgeLogo = () => {
    return localStorage.getItem(ODIN_LOGO_SWITCH_KEY) === "true";
  };

  private getProductBySku = (sku: string): Nullable<IProduct> => {
    const magentoProduct = MagentoHelper.magentoProducts.find(product => product.sku === sku);
    if (magentoProduct) {
      return magentoProduct;
    }

    const state = store.getState() as any;
    const fallbackProducts = [...(state.magento?.regProducts || []), ...(state.magento?.colocatedRegProducts || [])] as IProduct[];
    return fallbackProducts.find(product => product.sku === sku) || null;
  };

  private isLogoUrlAvailable = async (url: string): Promise<boolean> => {
    if (!url || !/^https?:\/\//i.test(url)) {
      return true;
    }

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return true;
    }

    const cachedResult = this._logoUrlAvailability.get(url);
    if (typeof cachedResult === "boolean") {
      return cachedResult;
    }

    return new Promise(resolve => {
      const xhr = new XMLHttpRequest();
      xhr.onload = () => {
        const isAvailable = xhr.status >= 200 && xhr.status < 300;
        this._logoUrlAvailability.set(url, isAvailable);
        resolve(isAvailable);
      };
      xhr.onerror = () => {
        this._logoUrlAvailability.set(url, false);
        resolve(false);
      };

      xhr.open("GET", url);
      xhr.responseType = "blob";
      xhr.send();
    });
  };

  private getCachedCmsEventDetails = async (eventEdition: EventEditionDetails): Promise<ICMSApiResponse | null> => {
    const currentEventEdition = store.getState().eventEdition.eventEdition;
    if (currentEventEdition?.gbsCode === eventEdition.gbsCode) {
      return store.getState().eventEdition.cmsEventDetails as ICMSApiResponse | null;
    }

    let cached: Record<string, ICMSApiResponse> = {};
    try {
      cached = (await localForage.getItem<Record<string, ICMSApiResponse>>(CMS_EVENT_DETAILS_STORAGE_KEY)) || {};
      if (cached[eventEdition.gbsCode]) {
        return cached[eventEdition.gbsCode] || null;
      }
    } catch (e) {
      console.warn("[colocated] unable to read cms event cache", e);
    }

    const response = await EventHelper.getBadgeLocation(eventEdition);
    try {
      const updatedCache = { ...cached, [eventEdition.gbsCode]: response.fullResponse };
      await localForage.setItem(CMS_EVENT_DETAILS_STORAGE_KEY, updatedCache);
    } catch (e) {
      console.warn("[colocated] unable to write cms event cache", e);
    }
    return (response.fullResponse as ICMSApiResponse) || null;
  };

  private resolveEventEditionForBadge = async (badge: IPersonBadge, isFreya = false) => {
    const normalizeGbs = (value?: string | null) => (value || "").trim().toUpperCase();
    const currentEventEdition = EventHelper.eventEdition;
    const badgeGbsCode = badge.eventEditionGbsCode;
    const normalizedBadgeGbsCode = normalizeGbs(badgeGbsCode);

    if (normalizedBadgeGbsCode && normalizeGbs(currentEventEdition?.gbsCode) === normalizedBadgeGbsCode) {
      return currentEventEdition;
    }

    if (normalizedBadgeGbsCode && isFreya) {
      const stored = await getStoredColocatedShowsForEvent(currentEventEdition);
      const match = stored.find(show => normalizeGbs(show.gbsCode) === normalizedBadgeGbsCode);
      if (match) {
        return match;
      }
    }

    return currentEventEdition || null;
  };

  getBadgeLogo = async (sku: string) => {
    if (!this.shouldPrintBadgeLogo()) {
      return "";
    }
    const cmsEventDetails = store.getState().eventEdition.cmsEventDetails;
    if (!cmsEventDetails || !EventHelper.eventEdition) {
      return "";
    }
    return CMSHelper.getCmsImageUrl(sku, "logo", EventHelper.eventEdition!, cmsEventDetails);
  };

  getBadgeSideImages = async (sku: string) => {
    if (!this.shouldPrintBadgeLogo()) {
      return ["", "", ""];
    }

    const cmsEventDetails = store.getState().eventEdition.cmsEventDetails;
    if (!cmsEventDetails || !EventHelper.eventEdition) {
      return ["", "", ""];
    }

    return [
      CMSHelper.getCmsImageUrl(sku, "image1", EventHelper.eventEdition!, cmsEventDetails),
      CMSHelper.getCmsImageUrl(sku, "image2", EventHelper.eventEdition!, cmsEventDetails),
      CMSHelper.getCmsImageUrl(sku, "image3", EventHelper.eventEdition!, cmsEventDetails)
    ];
  };

  getBadgeLogoForBadge = async (sku: string, badge: IPersonBadge, isFreya = false) => {
    if (!this.shouldPrintBadgeLogo()) {
      return "";
    }

    const eventEdition = await this.resolveEventEditionForBadge(badge, isFreya);
    if (!eventEdition) {
      return "";
    }

    const cmsEventDetails = await this.getCachedCmsEventDetails(eventEdition);
    if (!cmsEventDetails) {
      return "";
    }

    const defaultLogoUrl = CMSHelper.getCmsImageUrl(sku, "logo", eventEdition, cmsEventDetails);
    const product = this.getProductBySku(sku);

    if (product?.rx_shared_logo_usage !== 1) {
      return defaultLogoUrl;
    }

    const sharedLogoUrl = CMSHelper.getCmsImageUrl(sku, "logo", eventEdition, cmsEventDetails, {
      useSharedLogo: true
    });
    const sharedLogoAvailable = await this.isLogoUrlAvailable(sharedLogoUrl);

    if (sharedLogoAvailable) {
      return sharedLogoUrl;
    }

    return defaultLogoUrl;
  };

  getBadgeSideImagesForBadge = async (sku: string, badge: IPersonBadge, isFreya = false) => {
    if (!this.shouldPrintBadgeLogo()) {
      return ["", "", ""];
    }

    const eventEdition = await this.resolveEventEditionForBadge(badge, isFreya);
    if (!eventEdition) {
      return ["", "", ""];
    }

    const cmsEventDetails = await this.getCachedCmsEventDetails(eventEdition);
    if (!cmsEventDetails) {
      return ["", "", ""];
    }
    return [
      CMSHelper.getCmsImageUrl(sku, "image1", eventEdition, cmsEventDetails),
      CMSHelper.getCmsImageUrl(sku, "image2", eventEdition, cmsEventDetails),
      CMSHelper.getCmsImageUrl(sku, "image3", eventEdition, cmsEventDetails)
    ];
  };
}

const badgeImageHelper = new BadgeImageHelper();

export default badgeImageHelper;
